import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ImagePlus,
  Trophy,
  CalendarDays,
  MapPin,
  Users,
  ShoppingBag,
  X,
  ChevronRight,
  Medal,
  Landmark,
} from 'lucide-react';
import { t } from '../i18n';

// Gleiche Optionen wie das CreateEntryModal der Flutter-App
const OPTIONS = [
  // GANZ OBEN (Marcel 2026-08-31): Social-Media-Beitrag — öffnet den
  // Composer über den ?neu=1-Query-Param der Feed-Seite
  {
    icon: ImagePlus,
    label: t('web.component.create-entry-modal.beitrag'),
    description: t('web.component.create-entry-modal.bilder-text-fuer-den-community'),
    to: '/feed?neu=1',
  },
  {
    icon: Trophy,
    label: t('web.component.create-entry-modal.turnier'),
    description: t('web.component.create-entry-modal.erstelle-ein-neues-turnier'),
    to: '/verwaltung/turnier/erstellen',
  },
  {
    icon: CalendarDays,
    label: t('web.component.create-entry-modal.turnierserie'),
    description: t('web.component.create-entry-modal.erstelle-eine-neue-turnierserie'),
    to: '/verwaltung/turnierserie/erstellen',
  },
  {
    icon: MapPin,
    label: t('web.component.create-entry-modal.spielstaette'),
    description: t('web.component.create-entry-modal.erstelle-eine-neue-spielstaette'),
    to: '/verwaltung/spielstaette/erstellen',
  },
  {
    icon: Users,
    label: t('web.component.create-entry-modal.verein'),
    description: t('web.component.create-entry-modal.erstelle-einen-neuen-verein'),
    to: '/verwaltung/verein/erstellen',
  },
  {
    icon: ShoppingBag,
    label: t('web.component.create-entry-modal.shop'),
    description: t('web.component.create-entry-modal.erstelle-einen-neuen-shop'),
    to: '/verwaltung/shop/erstellen',
  },
  // Liga & Verband (2026-09-16)
  {
    icon: Medal,
    label: t('web.component.create-entry-modal.liga'),
    description: t('web.component.create-entry-modal.erstelle-eine-neue-dart-liga'),
    to: '/verwaltung/liga/erstellen',
  },
  {
    icon: Landmark,
    label: t('web.component.create-entry-modal.verband'),
    description: t('web.component.create-entry-modal.erstelle-einen-neuen-dart-verband'),
    to: '/verwaltung/verband/erstellen',
  },
];

interface CreateEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Auswahl "Neuer Eintrag" (Welle 5, Apple-Stil 2026-09-25): flache Zeilenliste
 * mit Trennlinien statt Karten – Mobile als Vollbild, ab sm: zentriertes
 * Sheet ohne Rahmen. Pendant zum CreateEntryModal der Flutter-App.
 */
const CreateEntryModal: React.FC<CreateEntryModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const select = (to: string) => {
    onClose();
    navigate(to);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-zinc-950 sm:items-center sm:justify-center sm:bg-black/70 sm:p-4 sm:backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={t('web.component.create-entry-modal.neuer-eintrag')}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-8 pt-4 sm:min-h-fit sm:w-full sm:max-w-md sm:flex-none sm:rounded-2xl sm:bg-zinc-950 sm:px-6 sm:pb-6 sm:pt-5" data-testid="create-entry-sheet">
        {/* Kopfzeile: Kapitälchen + Titel, Schließen als Ghost-Icon */}
        <div className="mb-2 flex items-center gap-2 border-b border-zinc-800 pb-4">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#3fced1]">{t('web.component.create-entry-modal.neuer-eintrag')}</p>
            <h2 className="truncate text-[22px] font-bold tracking-tight text-white">{t('web.component.create-entry-modal.waehle-den-typ-des-eintrags')}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('web.component.create-entry-modal.schliessen')}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-zinc-300 transition hover:bg-zinc-900 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Flache Zeilenliste mit Trennlinien */}
        <div>
          {OPTIONS.map(({ icon: Icon, label, description, to }) => (
            <button
              key={to}
              type="button"
              onClick={() => select(to)}
              className="flex w-full items-center gap-3 border-b border-zinc-800 py-3 text-left transition hover:bg-zinc-900/60 last:border-b-0"
              data-testid="create-entry-option"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#3fced1]/10">
                <Icon size={18} className="text-[#3fced1]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-medium text-white">{label}</span>
                <span className="block text-[13px] text-zinc-400">{description}</span>
              </span>
              <ChevronRight size={16} className="shrink-0 text-zinc-500" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CreateEntryModal;
